"use client";

import { useEffect, useRef } from "react";

import type { TrackedPill } from "@/lib/pill-counter/centroid-tracker";

interface LiveDotOverlayProps {
  pills: TrackedPill[];
  containerWidth: number;
  containerHeight: number;
}

export default function LiveDotOverlay({ pills, containerWidth, containerHeight }: LiveDotOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || containerWidth <= 0 || containerHeight <= 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = containerWidth;
    canvas.height = containerHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const pill of pills) {
      const x = (pill.x / 100) * canvas.width;
      const y = (pill.y / 100) * canvas.height;
      const r = Math.max(10, (pill.radius / 100) * canvas.width);
      const stable = pill.isStable;

      const dotColor = stable ? "rgba(0, 255, 180, 0.85)" : "rgba(255, 200, 0, 0.75)";
      const glowColor = stable ? "rgba(0, 255, 180, 0.18)" : "rgba(255, 200, 0, 0.15)";
      const borderColor = stable ? "rgba(0, 255, 180, 1)" : "rgba(255, 200, 0, 1)";

      ctx.beginPath();
      ctx.arc(x, y, r * 2, 0, Math.PI * 2);
      ctx.fillStyle = glowColor;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, y, r * 1.4, 0, Math.PI * 2);
      ctx.fillStyle = glowColor;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = dotColor;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }, [pills, containerWidth, containerHeight]);

  return (
    <canvas
      ref={canvasRef}
      width={containerWidth}
      height={containerHeight}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ mixBlendMode: "screen" }}
    />
  );
}
